import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "concrete-web-design-colorado",
  metaTitle: "Concrete Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom concrete web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Concrete contractor marketing", to: "/concrete-marketing-colorado" },
  ],
  currentLabel: "Concrete Contractor Web Design Colorado",
  eyebrow: "Concrete Contractor Web Design — Colorado",
  h1: (
    <>
      Concrete Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded concrete websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Concrete websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first concrete sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Concrete Contractor web design in Colorado",
    body: "Concrete Contractor web design is the practice of building conversion-focused, mobile-first websites for concrete companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with portfolio galleries that don't tank PageSpeed." },
    { title: "Service pages per project type", body: "Driveways, patios, stamped, foundations, retaining walls, decorative — each ranks independently." },
    { title: "Portfolio galleries", body: "Lazy-loaded project galleries with image schema for SEO." },
    { title: "Quote request forms", body: "Project-size and finish-type qualifying fields to filter for real buyers." },
    { title: "Service area pages", body: "Dedicated city pages with proper LocalBusiness schema." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Portfolio sells, copy supports", body: "Concrete is a portfolio sale — we surface project galleries above the fold, not buried on a \"gallery\" page." },
    { heading: "Stamped and decorative are different searches", body: "Stamped concrete, decorative overlays, and exposed aggregate all have unique search volume and competition." },
    { heading: "Most concrete sites are slow templates", body: "Wix and Squarespace concrete sites routinely score under 30. Hand-coded with proper image handling hits 95+." },
  ],
  relatedLinks: [
    { to: "/concrete-marketing-colorado", label: "Concrete contractor marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado concrete case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a concrete website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a concrete website?", a: "Typical timeline is 4–6 weeks for a 12–20 page concrete site with full service and service-area pages. Larger multi-location concrete companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs concrete companies?", a: "No — we build concrete sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Concrete Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for concrete companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/concrete-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
